import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useCallback, useState } from 'react';

import { Select } from '@ifs-gcp/components';
import type { Checklist, ChecklistRequirement } from '@ifs-gcp/core';

import { getTranslation } from '../../../../../../utils/translations';
import styles from './score-cell.module.scss';

type ScoreCellProps = {
  row: ChecklistRequirement;
  domain: Checklist;
};

export const ScoreCell = ({ row, domain }: ScoreCellProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const scoringsChanged =
    domain.isAdjustmentMode && row.score && row.previousResults?.score?.value !== row.score?.value;

  const handleDropdown = useCallback(
    (value: unknown) => {
      if (typeof value === 'string') {
        row.score = value;
      }
    },
    [row],
  );

  const handleToggle = (isOpen: boolean, e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsOpen(isOpen);
  };

  const scoringOptions = row.template.scoringOptions.length
    ? row.template.scoringOptions
    : domain.template.props.defaultScoringOptions;

  return (
    <button
      className={`${styles.cellContainer} ${scoringsChanged ? styles.highlighted : ''}`}
      onClick={() => setIsOpen(true)}
    >
      <Select
        className={styles.selectWrapper}
        classes={{
          select: styles.select,
          nativeInput: styles.select,
          icon: styles.selectIcon,
        }}
        displayEmpty
        onOpen={(e) => handleToggle(true, e)}
        onClose={(e) => handleToggle(false, e)}
        MenuProps={{
          anchorOrigin: {
            vertical: 'center',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        }}
        open={isOpen}
        value={row?.score?.scoreId || ''}
        renderValue={(value) =>
          value ? (
            getTranslation(
              scoringOptions.find((scoring) => scoring.scoreId === value)?.label,
              domain.language,
            )
          ) : (
            <MoreHorizIcon fontSize="medium" data-testid={`more-horiz-icon`} />
          )
        }
        options={scoringOptions.map(({ label, scoreId }) => ({
          label: getTranslation(label, domain.language),
          value: scoreId,
        }))}
        SelectDisplayProps={{
          tabIndex: -1,
        }}
        handleMenuItemClick={handleDropdown}
      />
    </button>
  );
};
